import type { Absence, BankHoliday, Booking } from '@/types';

export interface UtilisationSignatureInputs {
  bookings: readonly Booking[];
  absences: readonly Absence[];
  bankHolidays: readonly BankHoliday[];
  startDate: Date;
  endDate: Date;
}

/**
 * Content fingerprint of everything the resource utilisation badges derive from.
 * The booking array is rebuilt on unrelated state changes, so repainting every
 * resource cell whenever its identity changes costs a full column refresh for
 * numbers that did not move.
 */
export function utilisationSignature({
  bookings,
  absences,
  bankHolidays,
  startDate,
  endDate,
}: UtilisationSignatureInputs): string {
  // FNV-1a over the fields the utilisation calculation reads, so the signature
  // stays a short string no matter how many bookings are in range.
  let hash = 0x811c9dc5;
  const add = (value: string | number | null | undefined): void => {
    const text = value == null ? '' : String(value);
    for (let i = 0; i < text.length; i++) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    hash ^= 0x1f;
    hash = Math.imul(hash, 0x01000193);
  };

  for (const booking of bookings) {
    add(booking.id);
    add(booking.resourceId);
    add(booking.resourceIds?.join(','));
    add(booking.startTime);
    add(booking.endTime);
    add(booking.durationType);
    add(booking.halfDayPeriod);
    add(booking.durationHours);
    add(booking.bookingTypeId);
    add(booking.status);
  }
  for (const absence of absences) {
    add(absence.id);
    add(absence.resourceId);
    add(absence.startDate);
    add(absence.endDate);
    add(absence.startTime);
    add(absence.endTime);
    add(absence.type);
  }
  for (const holiday of bankHolidays) {
    add(holiday.id);
    add(holiday.date);
  }

  return [
    bookings.length,
    absences.length,
    bankHolidays.length,
    startDate.getTime(),
    endDate.getTime(),
    (hash >>> 0).toString(36),
  ].join(':');
}
